import {ConversationTeamStatusesEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {AnalyticsFilterTypesEnum} from '../../../../models/api/analyticsSavedViewModel';
import {InboxesFiltersEnum} from '../../../../models/url/models/inboxesUrlModel';
import {
  ConversationSelector,
  makeGetConversationTeamInboxes,
} from '../../../../store/data/conversations/conversationsSelectors';
import {getSortOptions} from '../../../../store/data/localPreferences/localPreferencesSelectors';
import {getShouldUseOpenFilterForTeams} from '../../../../store/data/preferences/preferencesSelectors';
import {AnalyticsPanelIdSelector} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {makeGetAnalyticsFilter} from '../../../../store/panels/analytics/filters/analyticsPanelFiltersSelectors';
import {
  InboxesConversationsParameters,
  InboxesParametersTypesEnum,
} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {normalInboxesSelection} from '../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {
  buildParametersFromTemplate,
  InboxesParametersTemplate,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersSelectors';

/**
 * This selector attemps to build a `InboxesConversationsParameters` for a shared inbox, in which the given conversation should be present.
 */
export function makeGetOptionalInboxesParametersForConversation<P>(
  getConversation: ConversationSelector<P>,
  getPanelId: AnalyticsPanelIdSelector<P>,
) {
  const getConversationTeamInboxes = makeGetConversationTeamInboxes(getConversation);
  const getSelectedInboxIds = makeGetAnalyticsFilter(
    AnalyticsFilterTypesEnum.LIVE_DASHBOARD_INBOXES,
    getPanelId,
  );

  return createSelector(
    [
      getConversation,
      getConversationTeamInboxes,
      getShouldUseOpenFilterForTeams,
      getSortOptions,
      getSelectedInboxIds,
    ],
    (conversation, conversationTeamInboxes, shouldUseOpenFiltersForTeams, sortOptions, selectedInboxIds) => {
      if (conversationTeamInboxes.length === 0) {
        return undefined;
      }

      // Find an inbox that is in both the conversation inboxes, and the dashboard selected inboxes.
      const conversationSelectedInbox = conversationTeamInboxes.find((inbox) =>
        selectedInboxIds?.includes(inbox.id),
      );

      // This shouldn't happen, but the first element of `conversationTeamInboxes` is a sane default.
      const inbox = conversationSelectedInbox ?? conversationTeamInboxes[0];

      const openFilters = shouldUseOpenFiltersForTeams
        ? [InboxesFiltersEnum.OPEN]
        : [InboxesFiltersEnum.UNASSIGNED, InboxesFiltersEnum.ASSIGNED];

      const waitingFilters = inbox.isTicketingEnabled
        ? [InboxesFiltersEnum.WAITING]
        : [InboxesFiltersEnum.SNOOZED];

      // Conversations in the Live dashboard cannot be resolved.
      // We can look at the team status to decide between Open and Waiting filters.
      const filters =
        conversation.teamStatus === ConversationTeamStatusesEnum.OPEN ? openFilters : waitingFilters;

      const parametersTemplate: InboxesParametersTemplate = {
        type: InboxesParametersTypesEnum.INBOX,
        inboxId: inbox.id,
        filters,
        selection: normalInboxesSelection([conversation.id]),
      };

      const parameters: InboxesConversationsParameters = buildParametersFromTemplate(
        sortOptions,
        parametersTemplate,
        {},
      );

      return parameters;
    },
  );
}
